// Source of truth for the per-quote pricing constants. Imported by the
// server-side calculator and the client QuoteProvider via the `@shared`
// alias. Changes here ripple to both the patient-facing estimate and the
// invoice PDF — keep them in sync intentionally.

export const PRICING_CONFIG = {
  // OR-cost block, applied once per quote (not per procedure).
  baseORCosts: {
    anesthesia: 1000,
    assistants: 1000,
    materials: 250,
    otherOrCosts: 250,
  },
  // Count-based surgeon fee, applied once per quote.
  surgeonFee: {
    firstProcedure: 1000,
    additionalProcedure: 500,
  },
} as const;

// Procedure flag that exempts a procedure from requiring general
// anesthesia. If every selected procedure carries this flag, the
// anesthesia line is dropped from the quote.
export const LOCAL_ANESTHESIA_ELIGIBLE_FLAG = "LOCAL_ANESTHESIA_ELIGIBLE";

// Procedure flag marking a "limited time offer" special. Specials are
// quoted at the `price` on the procedure record PLUS the risk-factor
// uplift (BMI, age, lost-50-lbs, smoker, …) — a higher-risk patient pays
// more than the advertised special. They still skip the sales-rep $
// modifier and the negotiable-fraction (photo) discount, so the special
// only ever moves UP from its advertised price, never below it.
//
// **If ANY procedure in the quote is a special, the OR-cost block AND
// surgeon fees are dropped from the entire quote** — including mixed
// quotes that pair a special with non-specials. The special's all-in
// price absorbs the facility/surgeon overhead even for the non-special
// procedures alongside it.
export const SPECIAL_FLAG = "SPECIAL";

// Procedure flag that suppresses ALL percentage risk-factor modifiers
// (auto: age/BMI/medical/lost-50-lbs/prior-surgery/smoker; and manual/
// custom %) for that procedure's line ONLY — the line is charged at
// exactly standardPrice (+ sales-rep $ modifier if applicable). Flat-$
// lump-sum modifiers and Discount lines are unaffected. Independent of
// SPECIAL_FLAG: SPECIAL + DISABLE_PRICE_MODIFIERS = face-value special;
// SPECIAL alone keeps the risk uplift (current behavior); this flag on a
// non-special procedure zeroes only its own % uplift while the shared
// OR-block/surgeon-fee/negotiable-fraction machinery still applies.
// DEPRECATED (Pricing v2, 2026-09): superseded by `enabledModifiers` in V2
// — still honored as the unset-default (all auto modifiers enabled when
// `enabledModifiers` is absent). Fully honored as-is in legacy mode.
export const DISABLE_PRICE_MODIFIERS_FLAG = "DISABLE_PRICE_MODIFIERS";

// Risk-factor modifiers derived from patient medical history. Each
// modifier's percentage is added to a per-procedure uplift; the sum is
// then capped at MODIFIER_CAP_PCT before applying to procedure prices.
//
// BMI tiers are inclusive on min, exclusive on max (e.g. 28 falls into
// the 28-32 bracket, 32 falls into 32-39). Age tiers follow the same
// convention. Out-of-range values resolve to 0%.
export type ModifierTier = { min: number; max: number; pct: number };

export const MODIFIER_RULES = {
  bmi: [
    { min: 18, max: 28, pct: 0 },
    { min: 28, max: 32, pct: 20 },
    { min: 32, max: 39, pct: 40 },
  ] as ModifierTier[],
  age: [
    { min: 18, max: 50, pct: 0 },
    { min: 50, max: 60, pct: 10 },
    { min: 60, max: 70, pct: 20 },
    { min: 70, max: Infinity, pct: 30 },
  ] as ModifierTier[],
  // User mapped "takes medication" (hasMedicalConditions toggle) to the
  // Rx-1 bracket: +10%. Rx 0-4 tiers in the spec are collapsed into a
  // single boolean since the patient form only has a yes/no toggle.
  // NOTE: when a numeric medicationCount is available (booking-calendar
  // flow), the perMedication rule below supersedes this boolean entirely —
  // the two are mutually exclusive; a 0-medication patient with this
  // switch on pays NO medication modifier under the count-based rule.
  medicalConditions: { yes: 10, no: 0 },
  // Count-based prescription-medication modifier. Applied INSTEAD of
  // medicalConditions when a numeric medicationCount is provided:
  // +pctPerMedication per Rx med, capped at capPct. Values are integer
  // pct points (10 = 10%, 50 = 50%) matching the convention above.
  perMedication: { pctPerMedication: 10, capPct: 50 },
  weightLossHx: { yes: 20, no: 0 },
  priorSurgery: { yes: 20, no: 0 },
  smoker: { yes: 20, no: 0 },
} as const;

export const MODIFIER_CAP_PCT = 100;

// DEPRECATED (Pricing v2, 2026-09): legacy-mode only. In V2
// (ESTIMATE_NEW_PRICING) the authored Procedure.minimumPrice replaces this
// derived floor and the fraction is pinned to 1. Do not remove until the
// legacy-pricing deprecation pass.
//
// Fraction of the modifier-applied procedure cost the sales rep can
// negotiate the patient down to. The "starting from" price the patient
// sees on the quote summary uses this. Base OR costs and surgeon fees
// are never discounted — only the per-procedure cost is.
//
// Two tiers:
//  - Default (no photos uploaded):    NEGOTIABLE_PROCEDURE_FRACTION
//  - Patient uploaded photos:         NEGOTIABLE_PROCEDURE_FRACTION
//                                       - PHOTO_UPLOAD_BONUS_FRACTION
//
// At baseline 0.75 the patient sees a 25% discount; uploading photos
// drops procedures to 0.50 of the modified price (an additional 25%
// off). The unlock is purely a marketing display — server-stored
// invoice totals still use the full modifier-applied amount.
export const NEGOTIABLE_PROCEDURE_FRACTION = 0.75;
export const PHOTO_UPLOAD_BONUS_FRACTION = 0.25;

export const MODIFIER_LABELS: Record<string, string> = {
  BMI: "BMI",
  AGE: "Age",
  MEDICAL_CONDITIONS: "Takes medication",
  WEIGHT_LOSS_HX: "Prior >50lb weight loss",
  PRIOR_SURGERY: "Prior cosmetic surgery",
  SMOKER: "Smoker",
  CUSTOM: "Custom adjustment",
  // Flat-dollar surcharge for surgical complexity. The COMPLEXITY_PRESETS
  // below give the three quick-add levels; a coordinator can also enter
  // any amount on a LUMP_SUM row.
  COMPLEXITY: "Increased complexity",
};

// Patient-facing label for the merged risk/complexity surcharge row on
// estimates and invoices (owner rename 2026-09-09; formerly "Personalized
// surgical care and added complexity").
export const COMPLEXITY_FEE_LABEL = "Surgical and Medical History Complexity Fee";

// Quick-add presets for the "Increased Complexity" lump-sum surcharges.
// Single source of truth for both the "Add modifier" dropdown and the
// option labels in the modifiers panel. `amount` is whole dollars
// (matches the calculator/config unit convention — cents conversion
// happens only at the invoice/snapshot boundary).
export type ComplexityPreset = { label: string; amount: number };
export const COMPLEXITY_PRESETS: ComplexityPreset[] = [
  { label: "Increased Complexity Level I", amount: 1000 },
  { label: "Increased Complexity Level II", amount: 2500 },
  { label: "Increased Complexity Level III", amount: 5000 },
];

// Default lump-sum amount when a row is switched to "$ Lump Sum" without
// picking a preset (e.g. a CUSTOM row). Coordinator types the real value.
export const MODIFIER_DEFAULT_AMOUNT = 0;

// Default percentages used by the admin "Add modifier" UI when a
// coordinator inserts a manual modifier without patient-derived data.
// AUTO-derived modifiers still use MODIFIER_RULES (tiered by BMI/age etc).
// CUSTOM stays at 0 so the coordinator picks their own number.
export const MODIFIER_DEFAULT_PCT: Record<string, number> = {
  BMI: 20,
  AGE: 10,
  MEDICAL_CONDITIONS: 10,
  WEIGHT_LOSS_HX: 20,
  PRIOR_SURGERY: 20,
  SMOKER: 20,
  CUSTOM: 0,
};
